import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

/**
 * Card destacado dentro do ImportDialog que oferece o download do modelo pronto
 * (CSV ou XLSX) para primeiros usos. Servido pelo backend em
 * `/entregas/import/template.{csv,xlsx}` — mantém uma única fonte de verdade
 * das colunas esperadas e 3 linhas de exemplo já preenchidas.
 *
 * Extraído do ImportDialog em fev/2026 (code-quality report):
 * - Isola o estado de "downloading" para não poluir o dialog raiz
 * - Reutilizável em fluxos futuros (ex.: importação de bairros/clientes)
 */
export function ImportTemplateCard() {
  const [templating, setTemplating] = useState(null); // 'csv' | 'xlsx' | null

  const downloadTemplate = async (kind) => {
    setTemplating(kind);
    try {
      const r = await api.get(`/entregas/import/template.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `modelo_importacao_entregas.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Modelo ${kind.toUpperCase()} baixado — preencha e volte aqui pra importar`);
    } catch (e) {
      console.error("Falha ao baixar template", kind, e);
      toast.error(e?.response?.data?.detail || `Erro ao baixar modelo ${kind.toUpperCase()}`);
    } finally { setTemplating(null); }
  };

  return (
    <Card className="border-orange-200 bg-orange-50/40" data-testid="template-download-card">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="p-2.5 rounded-lg bg-orange-600 text-white shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold text-orange-900">Primeiro uso? Baixe o modelo pronto</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Modelo com <b>colunas corretas</b> e <b>3 linhas de exemplo</b> (Dinheiro c/ troco, Cartão, PIX). Preencha, salve e volte aqui.
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("xlsx")}
              disabled={templating === "xlsx"}
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
              data-testid="btn-template-xlsx"
            >
              {templating === "xlsx"
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />XLSX</>
                : <><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-700" />Modelo XLSX</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("csv")}
              disabled={templating === "csv"}
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
              data-testid="btn-template-csv"
            >
              {templating === "csv"
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />CSV</>
                : <><FileText className="w-3.5 h-3.5 mr-1.5 text-blue-700" />Modelo CSV</>}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
